<script>
  import { scaleLinear, scaleTime } from 'd3-scale';
  import { extent, max } from 'd3-array';
  import { area, curveLinear, line } from 'd3-shape';
  import { csv } from 'd3-fetch';
  import { onMount } from 'svelte';

  // Data and dimensions
  let data = $state([]);
  let width = $state(475); // width will be set by the clientWidth
  const height = 250;
  const margin = { top: 0, right: 20, bottom: 56, left: 30 };

  // Line generator (reactive with data and scales)
  let xScale = $derived(
    scaleTime()
      .domain(extent(data, (d) => new Date(d.date)))
      .range([margin.left, width - margin.right])
  );

  let yScale = $derived(
    scaleLinear()
      .domain([0, max(data, (d) => Math.max(+d.type1, +d.type2))])
      .range([height - margin.bottom, margin.top])
  );

  // Generalized line and area generator functions
  function generateLinePath(data, yField) {
    return line()
      .x((d) => xScale(new Date(d.date)))
      .y((d) => yScale(+d[yField]))
      .curve(curveLinear)(data);
  }

  function generateAreaPath(data, yField) {
    return area()
      .x((d) => xScale(new Date(d.date)))
      .y0(height - margin.bottom)
      .y1((d) => yScale(+d[yField]))
      .curve(curveLinear)(data);
  }

  // Load data on mount
  onMount(() => {
    csv('/data/payments.csv')
      .then((csvData) => (data = csvData))
      .catch((error) => console.error('Failed to load CSV data:', error));
  });

  const settings = [
    { category: 'Bank', color: 'rgba(141, 187, 251, 1)' }, // Blue
    { category: 'Amazon Pay', color: 'rgb(251, 141, 220)' } // Purple
  ];
</script>

<!-- Tooltip.svelte -->

<!-- Responsive container for SVG -->
<div
  class="relative min-w-full max-w-md rounded-xl p-6"
  bind:clientWidth={width}>
  <div class="flex w-full items-center justify-between pr-8">
    {#if data[3]}
      <!-- <div
        class="pointer-events-none absolute z-50 min-w-[120px] rounded-xl bg-white p-2 px-3 text-sm shadow-lg"
        style="top: {130}px; left: {xScale(
          new Date(data[3].date)
        )}px; transform: translate(-110%, -60%);">
        <p class="text-sm text-gray-500">{' 24 Nov 2024'}</p>

        {#each ['type1', 'type2', 'type3', 'type4', 'type5'] as type, i}
          <p class="text-sm" style="color:{settings[i].color}">
            {settings[i]['category']}:
            {data[3][type]} txns.
          </p>
        {/each}
      </div> -->
    {/if}
    <h2 class="font-semibold font-gray-600">Purchases by payment method</h2>
  </div>
  {#if data.length && width && xScale && yScale}
    <svg width={width - margin.left} {height}>
      <defs>
        <!-- Purple gradient -->
        <linearGradient id="lineShade1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(251, 141, 220, 0.6)" />
          <!-- purple color with opacity -->
          <stop offset="100%" stop-color="rgba(251, 141, 220, 0)" />
          <!-- faded to transparent -->
        </linearGradient>

        <!-- Blue gradient -->
        <linearGradient id="lineShade2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(141, 187, 251, 0.6)" />
          <!-- blue color with opacity -->
          <stop offset="100%" stop-color="rgba(141, 187, 251, 0)" />
          <!-- faded to transparent -->
        </linearGradient>
      </defs>

      <!-- X Axis  -->
      <g transform="translate(0,{height - margin.bottom})">
        <line
          stroke="currentColor"
          x1={margin.left - 6}
          x2={width - margin.right} />
        {#each xScale.ticks() as tick}
          {#if tick !== 0}
            <line
              stroke="currentColor"
              x1={xScale(tick)}
              x2={xScale(tick)}
              y1={0}
              y2={6} />
          {/if}
        {/each}
        {#each xScale.ticks(3) as tick}
          <text
            font-size="12px"
            class="stroke-gray-400"
            text-anchor="middle"
            x={xScale(tick)}
            y={16}>
            {tick.toLocaleString('default', { month: 'short' })}
          </text>
        {/each}
      </g>

      <!-- Y Axis -->
      <g transform="translate({margin.left},0)">
        <!-- yScale.domain() is an array with two elements min and max values 
    from the data, so we can use it to create the start and end point
    of our axis line -->
        <line
          stroke="currentColor"
          y1={yScale(0)}
          y2={yScale(yScale.domain()[1])} />
        <!-- Specify the number of ticks here -->
        {#each yScale.ticks(2) as tick}
          {#if tick !== 0}
            <line
              stroke="currentColor"
              x1={0}
              x2={-6}
              y1={yScale(tick)}
              y2={yScale(tick)} />
          {/if}

          <text
            fill="currentColor"
            text-anchor="end"
            font-size="10"
            dominant-baseline="middle"
            x={-9}
            y={yScale(tick)}>
            {tick}
          </text>
        {/each}
      </g>
      <text
        x={0}
        y={yScale(yScale.domain()[1] + 10)}
        class="fill-gray-400"
        font-size="12px"
        text-anchor="start">
        Million ($) ↑
      </text>

      <path
        class="area-path area-path-1"
        d={generateAreaPath(data, 'type1')}
        fill="url(#lineShade1)" />
      <path
        class="area-path area-path-2"
        d={generateAreaPath(data, 'type2')}
        fill="url(#lineShade2)" />

      <!-- Category Labels with Color Indicators -->
      <g transform={`translate(${margin.left}, ${height - 20})`}>
        {#each settings as d, i}
          <g transform={`translate(${(width - margin.left - margin.right) * (i + 1) / (settings.length + 1)}, -4)`}>
            <!-- Color box -->
            <rect
              style="border-radius:10px;"
              width="16"
              height="16"
              rx="4"
              ry="4"
              fill={d.color} />
            <!-- Category text -->
            <text
              class="fill-gray-400"
              x="20"
              y="10"
              font-size="10px"
              alignment-baseline="middle">{d.category}</text>
          </g>
        {/each}
      </g>
      <!-- Horizontal dotted line -->
      {#if data}
        <!-- <line
          x1={xScale(new Date(data[3]?.date))}
          x2={xScale(new Date(data[3]?.date))}
          y1={yScale.range()[0]}
          y2={yScale.range()[1]}
          stroke="#d1d5db"
          stroke-dasharray="4 4"
          stroke-width="2" />
        {#each ['type1', 'type2', 'type3', 'type4', 'type5'] as type, i}
          <circle
            cx={xScale(new Date(data[3].date))}
            cy={yScale(data[3][type])}
            r="3"
            fill={settings[i].color} />
        {/each} -->
      {/if}
    </svg>
  {/if}
</div>

<style>
  .area-path {
    opacity: 0.9;
    transition-property: opacity;
    transition-duration: 200ms;
    transition-timing-function: ease-in-out;
  }

  .area-path:hover {
    opacity: 1;
  }

  /* When any area-path is hovered, fade out all following siblings */
  .area-path:hover ~ .area-path {
    opacity: 0.2;
  }

  /* When any area-path is hovered, fade out all previous siblings */
  .area-path:has(~ .area-path:hover) {
    opacity: 0.2;
  }

  .area-path-1 {
    fill: url(#lineShade1);
  }

  .area-path-2 {
    fill: url(#lineShade2);
  }
</style>
